import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { usePeriodDates } from '../../../hooks/usePeriodDates'
import { fetchInvoicesSummary } from '../query-api'
import { InvoicesSummaryData } from '../types/invoicesSummaryData'
import { FetchSalesDocumentsSummaryParams } from '../types/salesDocument/fetchSalesDocumentsSummaryParams'

interface UseFetchInvoicesSummaryProps {
  customerId?: string
  enabled?: boolean
  period?: string
  searchQuery?: string
}

interface UseFetchInvoicesSummaryResponse {
  data?: InvoicesSummaryData
  isLoading: boolean
}

export const useFetchInvoicesSummary = ({
  customerId,
  enabled = true,
  period,
  searchQuery,
}: UseFetchInvoicesSummaryProps): UseFetchInvoicesSummaryResponse => {
  const { isLoading: isLoadingPeriodDates, fromDate, toDate } = usePeriodDates(period)

  const queryParams: FetchSalesDocumentsSummaryParams = useMemo(
    () => ({
      ...(customerId ? { customerId } : {}),
      ...(searchQuery ? { searchQuery } : {}),
      ...(fromDate && toDate ? { fromDate, toDate } : {}),
    }),
    [customerId, fromDate, searchQuery, toDate],
  )
  const queryKey: [QueryKeys, FetchSalesDocumentsSummaryParams] = useMemo(
    () => [QueryKeys.InvoicesSummary, queryParams],
    [queryParams],
  )

  const {
    data: invoicesSummaryData,
    isIdle,
    isLoading,
  } = useQuery(queryKey, () => fetchInvoicesSummary(queryParams), {
    enabled: enabled && !isLoadingPeriodDates,
    keepPreviousData: true,
    staleTime: defaultStaleTime,
  })

  return {
    data: invoicesSummaryData?.data?.summary,
    isLoading: isIdle || isLoading,
  }
}
